/**
 * @file VatScreen.tsx
 *
 * Routed wrapper around {@link VatForm} for the `/payments/vat` route. Loads the
 * VAT settings, seeds the form, and owns persistence: on save it PATCHes the VAT
 * patch (via `/v1/shops/settings/tax`), toasts the outcome, and navigates back to
 * the Payments overview. The seed is memoized so the form's controlled state isn't
 * reset on every render.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { fetchVatSettings, updateVatSettings } from '../../api/payments';
import type { VatSettings } from '../../api/payments-types';
import { VatForm } from './VatForm';
import { buildVatPatch, deriveVatFields } from './vat-form-utils';
import type { VatFormFields } from './vat-form-types';

/**
 * Renders the routed VAT & taxes screen.
 *
 * @returns The VAT & taxes screen (with a loading fallback).
 */
export function VatScreen() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<VatSettings>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  useEffect(() => {
    let active = true;
    void fetchVatSettings()
      .then((loaded) => {
        if (active) setSettings(loaded);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const seed = useMemo(() => deriveVatFields(settings), [settings]);

  const goBack = (): void => {
    navigate('/payments');
  };

  const handleSubmit = async (fields: VatFormFields): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    try {
      await updateVatSettings(buildVatPatch(fields));
      notifySuccess(intl.formatMessage(adminMessages.toastSaved));
      goBack();
    } catch {
      setSaveError(intl.formatMessage(adminMessages.paymentsVatSaveError));
      notifyError(intl.formatMessage(adminMessages.toastError));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gv-flex gv-justify-center gv-p-lg">
        <Loader condensed />
      </div>
    );
  }

  return (
    <VatForm
      seed={seed}
      saving={saving}
      saveError={saveError}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
